import React from 'react'
import { createPortal } from 'react-dom'
import { swalSuccess } from '../../../../functions/swalOptions'
import { useAppDispatch } from '../../../../hooks/redux'
import { type User } from '../../../../store/slices/userSlice'
import { type BsoUnitAnalyticsRow } from '../adminBsoUnitStatistics'
import { DEFAULT_MANUAL_ADVERTISEMENT_STATUS, isManualActionUserConfigured, manualActionUserConfig, MANUAL_ADVERTISEMENT_PORTAL_OPTIONS, MANUAL_ADVERTISEMENT_STATUS_OPTIONS } from './manualOperations.config'
import { createManualAdvertisement } from './manualAdvertisementActions'
import { ManualUserSelector } from './ManualUserSelector'
import { type ManualAdvertisementPortal, type ManualAdvertisementPortalLink, type ManualAdvertisementStatus, type ManualAdvertisementUserMode } from './manualAdvertisement.types'
import './ManualAdvertisementModal.scss'

const getDateLocal = (offsetDays = 0): string => {
    const date = new Date()
    date.setDate(date.getDate() + offsetDays)
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
    return date.toISOString().slice(0, 10)
}

const createPortalLink = (): ManualAdvertisementPortalLink => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    portal: '',
    link: '',
    comment: '',
    disabled: false
})

const formatPrice = (price: number | null): string => price == null ? '-' : `AED ${price.toLocaleString('en-US')}`

export const ManualAdvertisementModal = ({
    unit,
    users,
    onClose
}: {
    unit: BsoUnitAnalyticsRow | null
    users: User[]
    onClose: () => void
}): JSX.Element | null => {
    const dispatch = useAppDispatch()
    const [userMode, setUserMode] = React.useState<ManualAdvertisementUserMode>('existing-user')
    const [selectedUser, setSelectedUser] = React.useState<User | null>(null)
    const [status, setStatus] = React.useState<ManualAdvertisementStatus>(DEFAULT_MANUAL_ADVERTISEMENT_STATUS)
    const [startDate, setStartDate] = React.useState(() => getDateLocal())
    const [endDate, setEndDate] = React.useState(() => getDateLocal(30))
    const [trakheesiId, setTrakheesiId] = React.useState('')
    const [portalLinks, setPortalLinks] = React.useState<ManualAdvertisementPortalLink[]>([createPortalLink()])
    const [saving, setSaving] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    React.useEffect(() => {
        if (unit == null) return
        setUserMode('existing-user')
        setSelectedUser(null)
        setStatus(DEFAULT_MANUAL_ADVERTISEMENT_STATUS)
        setStartDate(getDateLocal())
        setEndDate(getDateLocal(30))
        setTrakheesiId('')
        setPortalLinks([createPortalLink()])
        setSaving(false)
        setError(null)
    }, [unit])

    React.useEffect(() => {
        if (unit == null) return
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !saving) onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [unit, saving, onClose])

    if (unit == null || typeof document === 'undefined') return null

    const manualUserIsConfigured = isManualActionUserConfigured()
    const userId = userMode === 'manual-bso' ? manualActionUserConfig.id : selectedUser?.id
    const usablePortalLinks = portalLinks.filter((link) => link.portal.trim() !== '' || link.link.trim() !== '')
    const invalidPortalLink = usablePortalLinks.find((link) => link.portal.trim() === '' || link.link.trim() === '' || !/^https?:\/\//i.test(link.link.trim()))
    const manualConfigError = userMode === 'manual-bso' && !manualUserIsConfigured
        ? 'Manual by BSO user is not configured. Add REACT_APP_BSO_MANUAL_ACTION_USER_ID.'
        : null
    const validationError =
        userMode === 'existing-user' && selectedUser == null
            ? 'Select an existing user.'
            : userMode === 'manual-bso' && !manualUserIsConfigured
                ? 'Manual by BSO user is not configured.'
                : status.trim() === ''
                    ? 'Advertisement status is required.'
                    : startDate.trim() === '' || endDate.trim() === ''
                        ? 'Advertisement start and end dates are required.'
                        : new Date(endDate).getTime() < new Date(startDate).getTime()
                            ? 'Advertisement end date cannot be before start date.'
                            : trakheesiId.trim() === ''
                                ? 'Trakheesi ID is required.'
                                : invalidPortalLink != null
                                    ? 'Each portal link needs a portal name and http(s) URL.'
                                    : null
    const submitDisabled = saving || validationError != null || userId == null
    const visibleError = error ?? manualConfigError ?? validationError

    const updatePortalLink = (id: string, patch: Partial<ManualAdvertisementPortalLink>): void => {
        setPortalLinks((prev) => prev.map((link) => link.id === id ? { ...link, ...patch } : link))
        setError(null)
    }

    const removePortalLink = (id: string): void => {
        setPortalLinks((prev) => prev.length <= 1 ? [createPortalLink()] : prev.filter((link) => link.id !== id))
        setError(null)
    }

    const handleSubmit = async (): Promise<void> => {
        if (submitDisabled || userId == null) {
            setError(validationError)
            return
        }

        setSaving(true)
        setError(null)
        const result = await dispatch(createManualAdvertisement({
            unit,
            userId,
            status,
            startDate,
            endDate,
            trakheesiId,
            portalLinks: usablePortalLinks
        }))

        if (result.isSuccessful) {
            setSaving(false)
            onClose()
            void swalSuccess('Advertisement has been created.', undefined, 'Success')
            return
        }

        setSaving(false)
        setError(result.error ?? 'Could not create advertisement.')
    }

    return createPortal(
        <div className="manual-advertisement__backdrop" onClick={saving ? undefined : onClose}>
            <div className="manual-advertisement__modal" role="dialog" aria-modal="true" aria-labelledby="manual-advertisement-title" onClick={(event) => event.stopPropagation()}>
                <header className="manual-advertisement__header">
                    <div>
                        <h2 id="manual-advertisement-title">Add Advertisement</h2>
                        <p>Create a normal advertisement record for the selected unit.</p>
                    </div>
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Close</button>
                </header>

                <div className="manual-advertisement__body">
                    <section className="manual-advertisement__unit">
                        <img src={unit.imageUrl} alt={unit.headline} />
                        <div>
                            <div className="manual-advertisement__unit-title">
                                <strong>{unit.headline}</strong>
                                {unit.listingType != null && <span className="badge badge-light-primary">{unit.listingType}</span>}
                            </div>
                            <span>{unit.propertyName}</span>
                            <small>{unit.areaName}{unit.unitNumber != null ? `, Unit ${unit.unitNumber}` : `, Ref ${unit.unitReference}`}</small>
                            <div className="manual-advertisement__unit-meta">
                                <span>Ref: {unit.unitReference ?? unit.unitId}</span>
                                <span>Type: {unit.unitType ?? unit.unitCategory ?? '-'}</span>
                                <span>Listing price: {formatPrice(unit.currentPrice)}</span>
                            </div>
                        </div>
                    </section>

                    <section className="manual-advertisement__panel">
                        <h3>Agent / Manual User</h3>
                        <div className="manual-advertisement__mode-toggle" role="group" aria-label="User source">
                            <button className={userMode === 'existing-user' ? 'active' : ''} type="button" disabled={saving} onClick={() => setUserMode('existing-user')}>
                                Existing User
                            </button>
                            <button className={userMode === 'manual-bso' ? 'active' : ''} type="button" disabled={saving} onClick={() => setUserMode('manual-bso')}>
                                Manual by BSO
                            </button>
                        </div>
                        {userMode === 'existing-user'
                            ? (
                                <ManualUserSelector
                                    users={users}
                                    selectedUserId={selectedUser?.id ?? null}
                                    disabled={saving}
                                    onSelect={(user) => {
                                        setSelectedUser(user)
                                        setError(null)
                                    }}
                                />
                            )
                            : (
                                <section className={`manual-advertisement__manual-user${manualUserIsConfigured ? '' : ' manual-advertisement__manual-user--error'}`}>
                                    <strong>Manual by BSO</strong>
                                    <span>Use {manualActionUserConfig.email || 'configured system account'} system account for external/manual operations.</span>
                                    {manualUserIsConfigured
                                        ? <small>User ID {String(manualActionUserConfig.id)}</small>
                                        : <small>Missing REACT_APP_BSO_MANUAL_ACTION_USER_ID.</small>}
                                </section>
                            )}
                    </section>

                    <section className="manual-advertisement__panel">
                        <h3>Advertisement Information</h3>
                        <div className="manual-advertisement__form-grid">
                            <div>
                                <label className="form-label" htmlFor="manual-advertisement-status">Status</label>
                                <select id="manual-advertisement-status" className="form-select form-select-sm" value={status} disabled={saving} onChange={(event) => { setStatus(event.target.value as ManualAdvertisementStatus); setError(null) }}>
                                    {MANUAL_ADVERTISEMENT_STATUS_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-advertisement-price">Advertised price</label>
                                <input id="manual-advertisement-price" className="form-control form-control-sm" type="text" value={formatPrice(unit.currentPrice)} disabled readOnly />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-advertisement-start-date">Start date *</label>
                                <input id="manual-advertisement-start-date" className="form-control form-control-sm" type="date" value={startDate} disabled={saving} onChange={(event) => { setStartDate(event.target.value); setError(null) }} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-advertisement-end-date">End date *</label>
                                <input id="manual-advertisement-end-date" className="form-control form-control-sm" type="date" value={endDate} disabled={saving} onChange={(event) => { setEndDate(event.target.value); setError(null) }} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-advertisement-trakheesi">Trakheesi ID *</label>
                                <input id="manual-advertisement-trakheesi" className="form-control form-control-sm" type="text" value={trakheesiId} disabled={saving} placeholder="253479128002" onChange={(event) => { setTrakheesiId(event.target.value); setError(null) }} />
                            </div>
                        </div>
                    </section>

                    <section className="manual-advertisement__panel">
                        <div className="manual-advertisement__panel-head">
                            <h3>Portal Links</h3>
                            <button className="btn btn-sm btn-light" type="button" disabled={saving} onClick={() => setPortalLinks((prev) => [...prev, createPortalLink()])}>
                                Add portal link
                            </button>
                        </div>
                        <div className="manual-advertisement__portal-list">
                            {portalLinks.map((portalLink, index) => (
                                <div className="manual-advertisement__portal-row" key={portalLink.id}>
                                    <div className="manual-advertisement__portal-row-head">
                                        <strong>Portal #{index + 1}</strong>
                                        <button className="btn btn-sm btn-light" type="button" disabled={saving} onClick={() => removePortalLink(portalLink.id)}>Remove</button>
                                    </div>
                                    <div className="manual-advertisement__form-grid">
                                        <div>
                                            <label className="form-label" htmlFor={`manual-advertisement-portal-${portalLink.id}`}>Portal name</label>
                                            <select id={`manual-advertisement-portal-${portalLink.id}`} className="form-select form-select-sm" value={portalLink.portal} disabled={saving} onChange={(event) => updatePortalLink(portalLink.id, { portal: event.target.value as ManualAdvertisementPortal })}>
                                                <option value="">Select portal</option>
                                                {MANUAL_ADVERTISEMENT_PORTAL_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="form-label" htmlFor={`manual-advertisement-link-${portalLink.id}`}>Portal link</label>
                                            <input id={`manual-advertisement-link-${portalLink.id}`} className="form-control form-control-sm" type="url" value={portalLink.link} disabled={saving} placeholder="https://..." onChange={(event) => updatePortalLink(portalLink.id, { link: event.target.value })} />
                                        </div>
                                    </div>
                                    <label className="form-label" htmlFor={`manual-advertisement-comment-${portalLink.id}`}>Comment</label>
                                    <textarea id={`manual-advertisement-comment-${portalLink.id}`} className="form-control form-control-sm" rows={2} value={portalLink.comment} disabled={saving} placeholder="Optional" onChange={(event) => updatePortalLink(portalLink.id, { comment: event.target.value })} />
                                </div>
                            ))}
                        </div>
                    </section>

                    {visibleError != null && (
                        <div className="alert alert-light-danger mb-0">{visibleError}</div>
                    )}
                </div>

                <footer className="manual-advertisement__footer">
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn btn-sm btn-primary" type="button" onClick={() => { void handleSubmit() }} disabled={submitDisabled}>
                        {saving ? 'Creating...' : 'Create Advertisement'}
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    )
}
